import type { Event } from '@sentry/capacitor';

import { normalizeApiEndpoint } from './normalize-api-endpoint.util';

const UNKNOWN_API_MESSAGE_PREFIX = 'Unknown API error:';

/**
 * True only for the api-consumer's `Unknown API error` captureMessage events.
 * Scoped to the message prefix (not the `http_status` tag) so a future,
 * unrelated event that happens to carry an `http_status` tag is never swept in
 * — and, critically, never silently dropped when its status is 0.
 */
function isUnknownApiErrorEvent(event: Event): boolean {
  return (
    typeof event.message === 'string' &&
    event.message.startsWith(UNKNOWN_API_MESSAGE_PREFIX)
  );
}

/** The request url the api-consumer always records under `extra.url`. */
function extractUrl(event: Event): string | null {
  const extraUrl = event.extra?.['url'];
  return typeof extraUrl === 'string' && extraUrl.length > 0 ? extraUrl : null;
}

/**
 * Sentry `beforeSend` transform for our `Unknown API error` events:
 *
 * - status 0 → `null` (drop). Angular reports status 0 for a request that never
 *   completed — network / CORS / aborted navigation. On our high-traffic public
 *   pages these are dominated by whole-page-teardown aborts and bot probes; the
 *   residual real-network bucket has no actionable signal (steady, no release
 *   tag, 0 users), so dropping them removes noise without losing anything.
 * - status ≥ 400 → fingerprint `['unknown-api', <status>, <normalized endpoint>]`
 *   so real 4xx/5xx split into per-endpoint issues instead of one mega-group.
 * - anything else / non-API events → returned unchanged.
 *
 * Never throws; the caller wraps it for the total-function guarantee. Generic
 * over the concrete event type (e.g. Sentry's narrower `ErrorEvent`) so a kept
 * event keeps its exact type for `beforeSend`'s signature.
 */
export function fingerprintApiErrorEvent<T extends Event>(event: T): T | null {
  if (!isUnknownApiErrorEvent(event)) {
    return event;
  }

  const httpStatus = event.tags?.['http_status'];

  if (httpStatus === '0') {
    return null;
  }

  const statusNumber = Number(httpStatus);
  if (Number.isFinite(statusNumber) && statusNumber >= 400) {
    event.fingerprint = [
      'unknown-api',
      String(httpStatus),
      normalizeApiEndpoint(extractUrl(event)),
    ];
  }

  return event;
}
